import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Link as LinkIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  PauseCircleOutline as PausaIcon,
  PlayCircleOutline as ReanudarIcon,
} from "@mui/icons-material";
import RichContent from "../public/RichContent";
import PostulacionesTema from "./PostulacionesTema";
import PropuestaTema from "./PropuestaTema";
import CrearTemaDialog from "./CrearTemaDialog";
import EstrellaFavorito from "./EstrellaFavorito";
import BotonVolver from "../kit/BotonVolver";
import EstadoChip from "../kit/EstadoChip";
import Etiqueta from "../kit/Etiqueta";
import { useTema, useDarAccesoCoguia, useDeleteTema, usePausarTema } from "../../queries/useTemas";
import { usePostular, useRetirarPostulacion } from "../../queries/usePostulaciones";
import { TemaApp } from "../../types/tema";
import { useAuthStore } from "../../stores/authStore";
import { categoriaDe } from "../../utils/categoria";
import { formatFecha } from "../../utils/fecha";
import { chipDeTema } from "../../utils/estados";
import { mensajeDeError } from "../../utils/errores";
import { urlPublicaDeTema } from "../../utils/enlaces";

/** Cuánto dura el ✓ en el botón de copiar antes de volver a su estado normal. */
const AVISO_MS = 2000;

/**
 * Ficha de un tema, con URL propia y compartible (`/temas/:id` público,
 * `/app/temas/:id` dentro de la app). Sin sesión el backend resuelve los temas
 * disponibles y asignados, así que un tema en revisión responde 404 al visitante
 * anónimo.
 *
 * Según quién mire: el estudiante postula, y el guía del tema ve las solicitudes
 * recibidas y las acepta o rechaza.
 */
/** Le da cuenta al coguía externo para que pueda evaluar (solo el coordinador).
 *
 *  Si no tiene RUT queda con una contraseña aleatoria que nadie conoce, y le llega un
 *  correo avisándole que ya puede entrar, con el enlace para elegir una. */
function AccesoCoguiaExterno({ temaId, esExterno, tieneCorreo }: { temaId: number; esExterno: boolean; tieneCorreo: boolean }) {
  const dar = useDarAccesoCoguia();

  // El aviso va primero y no depende de `esExterno`: darle acceso es justamente lo que
  // deja de serlo, y si el bloque se desmontara ahí, el coordinador apretaría el botón
  // y no vería nada más que el correo desapareciendo de la ficha.
  if (dar.isSuccess) {
    const { persona, creada } = dar.data;
    return (
      <Alert severity="success" sx={{ py: 0.5 }}>
        {creada
          ? `${persona.nombre} quedó registrado y ya puede evaluar.`
          : `Se enlazó con ${persona.nombre}, que ya estaba en el sistema.`}{" "}
        Le llegó un correo con el enlace para elegir su contraseña.
      </Alert>
    );
  }

  if (!esExterno) return null;

  if (!tieneCorreo) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sin correo anotado no se le puede dar acceso: agrégalo editando el tema.
      </Typography>
    );
  }

  return (
    <Box>
      <Button size="small" variant="outlined" disabled={dar.isPending} onClick={() => dar.mutate(temaId)}>
        {dar.isPending ? "Registrando…" : "Darle acceso para evaluar"}
      </Button>
      {dar.isError && (
        <Alert severity="error" sx={{ mt: 0.75, py: 0.5 }}>
          {mensajeDeError(dar.error, "No se pudo dar acceso.")}
        </Alert>
      )}
    </Box>
  );
}

export default function TemaDetalleView() {
  const { id } = useParams<{ id: string }>();
  const temaId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const esCoordinador = !!user?.es_coordinador_global;
  const { data: tema, isLoading, isError } = useTema(temaId);
  const postular = usePostular(temaId);
  const retirar = useRetirarPostulacion(temaId);
  const eliminar = useDeleteTema();
  const pausar = usePausarTema();
  const [postulando, setPostulando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [copia, setCopia] = useState<"" | "ok" | "error">("");
  const [confirmarRetiro, setConfirmarRetiro] = useState(false);

  const volverA = user ? "/app/temas" : "/temas";
  // El proponente mirando su propia propuesta: retirarla borra el tema, no solo la solicitud.
  const esPropuestaPropia = !!tema?.es_propuesta_estudiante && !!tema?.mi_postulacion;

  // Se copia siempre la dirección pública, no la que se está mirando: es la
  // única que abre alguien de afuera (y para quien tiene sesión funciona igual).
  // La confirmación queda en el botón, que es donde está mirando quien lo pulsó.
  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(urlPublicaDeTema(temaId));
      setCopia("ok");
    } catch {
      // Si el navegador niega el portapapeles el botón lo dice y deja la
      // dirección a la vista, en vez de quedarse mudo.
      setCopia("error");
    }
    setTimeout(() => setCopia(""), AVISO_MS);
  };

  const enviarPostulacion = () => {
    postular.mutate(mensaje, {
      onSuccess: () => {
        setPostulando(false);
        setMensaje("");
      },
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !tema) {
    return (
      <Box>
        <BotonVolver fallback={volverA} etiquetaFallback="Temas" />
        {/* Sin sesión hay temas que no se resuelven (los que están en revisión), y
            aquí llega quien abrió un link de la app: se le deja el login a mano. */}
        <Alert
          severity="warning"
          action={
            user ? undefined : (
              <Button color="inherit" size="small" onClick={() => navigate("/login")}>
                Iniciar sesión
              </Button>
            )
          }
        >
          {user ? "Este tema no existe." : "Este tema no existe o no está disponible públicamente. Inicia sesión para verlo."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <BotonVolver fallback={volverA} etiquetaFallback="Temas" />

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, border: "1px solid", borderColor: "divider", borderRadius: "9px" }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
          {tema.areas && tema.areas.length > 0 ? (
            tema.areas.map((area) => <Etiqueta key={area}>{area}</Etiqueta>)
          ) : (
            <Etiqueta>{categoriaDe(tema)}</Etiqueta>
          )}
          {tema.estado && <EstadoChip {...chipDeTema(tema)} />}
        </Stack>

        {/* La estrella solo con sesión: es la marca de alguien, y sin sesión no hay quién. */}
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2.5 }}>
          <Typography variant="h4" sx={{ flexGrow: 1, minWidth: 0 }}>
            {tema.titulo}
          </Typography>
          {user && <EstrellaFavorito temaId={temaId} favorito={!!tema.es_favorito} tamano={26} />}
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 6 }} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          <Dato label="Guía" value={tema.guia ? `${tema.guia}${tema.guia_email ? ` (${tema.guia_email})` : ""}` : "—"} muted={!tema.guia} />
          {tema.coguia && (
            <Stack spacing={0.75}>
              <Dato
                label="Coguía | Externo"
                value={tema.coguia_es_externo && tema.coguia_email ? `${tema.coguia} (${tema.coguia_email})` : tema.coguia}
              />
              {/* Un coguía externo es solo texto: no tiene cuenta y por eso no puede
                  evaluar aunque el paso lo incluya en la comisión. Esto lo convierte
                  en persona del sistema, y es del coordinador. */}
              {esCoordinador && (
                <AccesoCoguiaExterno temaId={temaId} esExterno={!!tema.coguia_es_externo} tieneCorreo={!!tema.coguia_email} />
              )}
            </Stack>
          )}
          {tema.fecha_creacion && <Dato label="Creado" value={formatFecha(tema.fecha_creacion)} />}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {tema.descripcion ? (
          <RichContent html={tema.descripcion} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            Este tema no tiene descripción.
          </Typography>
        )}

        {/* Estudiante que ya postuló (o propuso): el estado de su solicitud. Retirar una
            propuesta borra el tema, así que se confirma antes. */}
        {tema.mi_postulacion && (
          <Alert
            severity={estadoSeveridad(tema.mi_postulacion.estado)}
            sx={{ mt: 3 }}
            action={
              tema.mi_postulacion.estado === "pendiente" ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={retirar.isPending}
                  onClick={() => (esPropuestaPropia ? setConfirmarRetiro(true) : retirar.mutate(tema.mi_postulacion!.id))}
                >
                  {esPropuestaPropia ? "Retirar propuesta" : "Retirar"}
                </Button>
              ) : undefined
            }
          >
            {esPropuestaPropia ? (
              <>
                {tema.mi_postulacion.estado === "pendiente" && "Propusiste este tema. El profesor guía aún no responde."}
                {tema.mi_postulacion.estado === "aceptada" && "El profesor guía aprobó tu propuesta."}
                {tema.mi_postulacion.estado === "rechazada" && "El profesor guía rechazó tu propuesta."}
              </>
            ) : (
              <>
                {tema.mi_postulacion.estado === "pendiente" && "Ya postulaste a este tema. El guía aún no responde."}
                {tema.mi_postulacion.estado === "aceptada" && "El guía aceptó tu postulación a este tema."}
                {tema.mi_postulacion.estado === "rechazada" && "Tu postulación a este tema fue rechazada."}
              </>
            )}
          </Alert>
        )}

        {retirar.isError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {mensajeDeError(retirar.error, "No se pudo retirar la postulación.")}
          </Alert>
        )}

        {/* Estudiante que mira un tema al que podría postular pero no le corresponde:
            porque no tiene proceso en curso, o porque ya tiene tema. El motivo lo arma
            el backend (`motivo_no_puede_postular`) para no repetir la regla acá. */}
        {tema.motivo_no_postula && (
          <Alert severity="info" sx={{ mt: 3 }}>
            {tema.motivo_no_postula}
          </Alert>
        )}

        {postular.isError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {mensajeDeError(postular.error, "No se pudo enviar la postulación.")}
          </Alert>
        )}

        {/* En pausa: al guía se le explica qué implica; a quien ya postuló, que su
            solicitud sigue en pie —el tema salió del catálogo, no su respuesta—. */}
        {tema.esta_en_pausa && (tema.es_guia || tema.mi_postulacion) && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {tema.es_guia
              ? "Este tema está en pausa: no aparece en el catálogo y no puede recibir postulaciones nuevas. Las que ya tienes siguen aquí y puedes resolverlas."
              : "El profesor puso este tema en pausa y no está recibiendo postulaciones nuevas. Tu solicitud sigue pendiente de respuesta."}
          </Alert>
        )}

        {pausar.isError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {mensajeDeError(pausar.error, "No se pudo cambiar la pausa del tema.")}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mt: 4 }} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          <Tooltip title={copia === "error" ? urlPublicaDeTema(temaId) : "El link se abre sin iniciar sesión"}>
            <Button variant="outlined" startIcon={copia === "ok" ? <CheckIcon /> : <LinkIcon />} onClick={copiarLink}>
              {copia === "ok" ? "Link copiado" : copia === "error" ? "No se pudo copiar" : "Copiar link"}
            </Button>
          </Tooltip>
          {/* Guía/coguía o coordinador: pausar, editar y eliminar el tema. */}
          {tema.puede_pausar && (
            <Button
              variant="outlined"
              startIcon={tema.esta_en_pausa ? <ReanudarIcon /> : <PausaIcon />}
              disabled={pausar.isPending}
              onClick={() => pausar.mutate({ id: temaId, pausar: !tema.esta_en_pausa })}
            >
              {tema.esta_en_pausa ? "Volver a publicar" : "Pausar tema"}
            </Button>
          )}
          {tema.es_guia && (
            <>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditando(true)}>
                Editar
              </Button>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmarBorrar(true)}>
                Eliminar
              </Button>
            </>
          )}
          {tema.puede_postular && (
            <Button variant="contained" onClick={() => setPostulando(true)}>
              Postular a este tema
            </Button>
          )}
          {!user && (
            <Button variant="contained" onClick={() => navigate("/login")}>
              Inicia sesión para postular
            </Button>
          )}
        </Stack>
      </Paper>

      {tema.es_guia && <CrearTemaDialog open={editando} onClose={() => setEditando(false)} tema={tema as TemaApp} />}

      <Dialog open={confirmarBorrar} onClose={() => setConfirmarBorrar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar tema</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que quieres eliminar <strong>{tema.titulo}</strong>? No se puede si está asignado o tiene postulaciones
            pendientes.
          </DialogContentText>
          {eliminar.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mensajeDeError(eliminar.error, "No se pudo eliminar el tema.")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarBorrar(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={eliminar.isPending}
            onClick={() => eliminar.mutate(temaId, { onSuccess: () => navigate(volverA) })}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmarRetiro} onClose={() => setConfirmarRetiro(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Retirar la propuesta</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Si retiras tu propuesta, <strong>el tema se elimina</strong> y tendrás que volver a proponerlo desde cero.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarRetiro(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={retirar.isPending}
            onClick={() =>
              retirar.mutate(tema.mi_postulacion!.id, {
                onSettled: () => setConfirmarRetiro(false),
                onSuccess: () => navigate(volverA),
              })
            }
          >
            Retirar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guía del tema (o coordinador). Una propuesta de estudiante tiene una sola
          solicitud —la suya—, así que se muestra como bloque en vez de tabla. */}
      {tema.es_guia &&
        (tema.es_propuesta_estudiante ? (
          <PropuestaTema temaId={temaId} admiteResolucion={!!tema.admite_resolucion} />
        ) : (
          <PostulacionesTema temaId={temaId} admiteResolucion={!!tema.admite_resolucion} />
        ))}

      <Dialog open={postulando} onClose={() => setPostulando(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Postular a este tema</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            El guía verá tu solicitud y podrá aceptarla o rechazarla. Puedes contarle por qué te interesa el tema.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Mensaje (opcional)"
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostulando(false)}>Cancelar</Button>
          <Button variant="contained" disabled={postular.isPending} onClick={enviarPostulacion}>
            Enviar postulación
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

const estadoSeveridad = (estado: string) =>
  estado === "aceptada" ? "success" : estado === "rechazada" ? "error" : ("info" as const);

/** Bloque etiqueta-valor (vertical), para mostrar guía y coguía lado a lado. */
function Dato({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: muted ? "text.disabled" : "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}
